import React, { FC, useEffect } from 'react'
import { useMemo } from 'react'
import useOnScreen from '../../../hooks/use-on-screen'
import ExampleStory from '../../../assets/img/example/story.svg'

import './LoadableImage.scss'
import { TExtendedPostAttachment } from '../../../store/managePost/newTypes'

export enum ASC_PREVIEW_SIZE {
  SMALL = 'small',
  MEDIUM = 'medium',
  ORIGINAL = 'original'
}

export enum LOADER_STYLE {
  DARK = 'dark',
  LIGHT = 'light'
}

export interface ILoadableImage {
  media: string | TExtendedPostAttachment
  loaderStyle?: LOADER_STYLE
  attachmentSize?: ASC_PREVIEW_SIZE
  key?: string
  containerWidth?: number
  isDisableAnimation?: boolean
  alt?: string
  onLoad?(): void
}
const LoadableImage: FC<ILoadableImage> = ({
  alt = '',
  media,
  attachmentSize,
  isDisableAnimation,
  key,
  containerWidth,
  loaderStyle = LOADER_STYLE.LIGHT,
  onLoad = () => {}
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  const imageRef = React.useRef<HTMLImageElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const isVisible = useOnScreen(containerRef)

  useEffect(() => {
    if (!isVisible || isLoaded) return

    const onLoadImage = () => {
      setIsLoaded(true)
      onLoad()
    }
    if (imageRef.current) {
      imageRef.current.onload = onLoadImage
    }
    return () => {
      if (imageRef.current) imageRef.current = null
    }
  }, [isVisible, onLoad, isLoaded])

  const imageData = useMemo(() => {
    let src, height

    if (typeof media !== 'string') {
      const imageAscSizes = Object.values(ASC_PREVIEW_SIZE)
      const sizeImageAscIndex = imageAscSizes.findIndex((size) => size === attachmentSize)
      const attachmentOptions = media?.object_data?.options

      for (let i = imageAscSizes.length - 1; i >= sizeImageAscIndex; i--) {
        src = media?.object_data?.previews?.[imageAscSizes[i]]
      }

      if (!src) src = media?.object_data?.public_url

      if (containerWidth && attachmentOptions) {
        const { height: attachmentHeight, width: attachmentWidth } = attachmentOptions
        height = (attachmentHeight * containerWidth) / attachmentWidth
      }
    } else {
      src = media
    }

    return {
      src,
      alt: alt || 'media',
      height: height || containerWidth
    }
  }, [attachmentSize, containerWidth, media, alt])

  return (
    <div
      key={key}
      ref={containerRef}
      className={`loadable-container ${isDisableAnimation && 'disable-animation'} ${loaderStyle} ${
        isLoaded && 'loaded'
      }`}
      style={{ height: imageData.height }}
    >
      {(isVisible || isLoaded) && (
        <img
          ref={imageRef}
          key={imageData.src || ExampleStory}
          className={`loadable-container__image ${isLoaded && 'loaded'}`}
          src={imageData.src || ExampleStory}
          alt={imageData.alt}
        />
      )}
    </div>
  )
}

export default LoadableImage
